import React, { useEffect, useState } from 'react';
import { InputText, Checkbox, Dropdown } from 'components/forms';
import { usePanel } from 'hooks/usePanel';
import { atelierRolesAPI } from 'services/swagger-api';
import { GetReferentielBriquesResponse } from 'api/atelier-roles';
import { portailLabel } from 'core/roles';
import { InlineError, runOperation } from 'back-office/shared/components/rolesUi';

interface CreateBriqueFormProps {
  referentiel: GetReferentielBriquesResponse;
  onSuccess: () => Promise<void>;
}

/**
 * Contenu du panel « Nouvelle brique » : création par duplication d'une brique
 * existante (copie des permissions au départ). La garde de nature reste côté
 * serveur — une brique personnalisée n'est attribuable qu'aux natures CRT/CIE.
 */
export const CreateBriqueForm: React.FC<CreateBriqueFormProps> = ({ referentiel, onSuccess }) => {
  const { setPanelActions, closePanel } = usePanel();

  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [nomBase, setNomBase] = useState(referentiel.briques[0]?.nom ?? '');
  // Préremplis avec ceux de la brique de base, et resynchronisés quand la base
  // change — jusqu'à ce que l'admin y touche lui-même.
  const [portails, setPortails] = useState<string[]>(referentiel.briques[0]?.portails ?? []);
  const [portailsTouched, setPortailsTouched] = useState(false);

  const changeBase = (nouvelleBase: string) => {
    setNomBase(nouvelleBase);
    if (!portailsTouched) {
      setPortails(referentiel.briques.find((b) => b.nom === nouvelleBase)?.portails ?? []);
    }
  };
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: isSaving || nom.trim().length === 0,
      onSubmit: async () => {
        setIsSaving(true);
        const err = await runOperation(() =>
          atelierRolesAPI.createBrique({
            createBriqueCommand: {
              nom: nom.trim(),
              description: description.trim() || null,
              nomBase,
              portails
            }
          })
        );
        setIsSaving(false);
        setError(err);
        if (!err) {
          await onSuccess();
          closePanel();
        }
      }
    });
  }, [setPanelActions, closePanel, onSuccess, nom, description, nomBase, portails, isSaving]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        La nouvelle brique démarre avec les permissions de la brique de base, puis s'ajuste librement.
        Une brique personnalisée n'est attribuable qu'aux courtiers et compagnies (garde de nature).
      </p>

      <InputText
        label="Nom"
        name="nom"
        required
        value={nom}
        onChange={(e) => setNom(e.target.value)}
        placeholder="Ex. Distribution restreinte"
      />

      <Dropdown
        name="nomBase"
        label="Dupliquer depuis"
        items={referentiel.briques.map((b) => ({ key: b.nom, label: b.nom }))}
        selectedItem={
          referentiel.briques
            .map((b) => ({ key: b.nom, label: b.nom }))
            .find((item) => item.key === nomBase) ?? null
        }
        onSelect={(item) => changeBase(String(item.key))}
      />

      <InputText
        label="Description"
        name="description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="À quoi sert cette brique ?"
      />

      <div>
        <span className="mb-1 block text-sm font-medium text-gray-700">Portails</span>
        <div className="flex flex-wrap gap-4">
          {referentiel.portails.map((portail) => (
            <Checkbox
              key={portail}
              name={`portail-${portail}`}
              label={portailLabel(portail)}
              checked={portails.includes(portail)}
              onChange={(checked) => {
                setPortailsTouched(true);
                setPortails((prev) =>
                  checked ? [...prev, portail] : prev.filter((p) => p !== portail)
                );
              }}
            />
          ))}
        </div>
      </div>

      <InlineError message={error} />
    </div>
  );
};
